import React, { useState } from "react";

import { Tooltip } from "@material-ui/core";
import { Button, useListContext, useNotify, useRefresh } from "react-admin";

import { ConditionalConfirm } from "../ConditionalConfirm";
import { LinkIcon } from "../iconography";
import { logError } from "../../../utils/appUtils";
import { CarrierType, OutboundStatus } from "../../../utils/constants";
import { isUserFacingError } from "../../../utils/lambdaErrors";
import { linkOutbounds } from "../../../utils/shipWith";

/** The lambda caps a group at ten loads (AD-7); the pre-check says so first. */
export const MAX_SHIP_WITH_GROUP = 10;

/** The only statuses a load can be linked in — before the truck exists. */
export const LINKABLE_STATUSES = [
  OutboundStatus.ADVICE_PENDING,
  OutboundStatus.PENDING,
];

export const UNSCHEDULE_FIRST_MESSAGE =
  "Unschedule all but one load before linking";

/**
 * Every id the link would put on one truck: the selection plus the members of
 * any group a selected row already belongs to (the lambda unions them).
 */
const resultingMemberIds = (rows) => {
  const ids = new Set();
  rows.forEach((row) => {
    ids.add(row.id);
    (row.shipWithIds || []).forEach((id) => ids.add(id));
  });
  return [...ids];
};

/**
 * A load's appointment is its Timeslot row when it has one — members of an
 * existing group share the same row, so they count once — else the bare
 * scheduled time a legacy row may carry. Null when unscheduled.
 */
const appointmentKey = (row) =>
  row?.timeslotId || row?.scheduledDateTime || null;

/**
 * Why the selection cannot be linked, or null when it can. These are the
 * lambda's own rules (AD-7) run against the list rows, so the user is told
 * before the round trip. `selectedCount` is the size of the selection;
 * react-admin keeps ids across pages but hands over only this page's rows, so
 * a shortfall means a row we cannot check.
 */
export const getShipsTogetherBlocker = (rows, selectedCount = rows.length) => {
  if (selectedCount < 2) return "Select at least two loads";
  if (rows.length < selectedCount)
    return "Some selected loads are not on this page. Clear the selection and select again.";
  if (new Set(rows.map((row) => row.accountId)).size > 1)
    return "All loads must belong to the same account";
  if (rows.some((row) => row.carrierType !== CarrierType.TRUCK))
    return "Only truck loads can ship together";
  if (rows.some((row) => row.transfer)) return "Transfers cannot ship together";
  if (rows.some((row) => !LINKABLE_STATUSES.includes(row.status)))
    return "Only advices awaiting review and pending loads can be linked";
  if (rows.some((row) => row.gateCheckInId))
    return "Cancel the truck's gate check-in before linking";
  const groups = new Set(
    rows.map((row) => row.shipWithGroupId).filter(Boolean),
  );
  if (groups.size > 1)
    return "Loads from two different groups cannot be merged";
  if (resultingMemberIds(rows).length > MAX_SHIP_WITH_GROUP)
    return `A group can hold at most ${MAX_SHIP_WITH_GROUP} loads`;
  return null;
};

/**
 * The distinct appointments among the rows, each represented by a row that
 * holds it. One means the others adopt it; two or more means the link is
 * refused until all but one are unscheduled (AD-7).
 */
export const getDistinctAppointments = (rows) => {
  const byKey = new Map();
  rows.forEach((row) => {
    const key = appointmentKey(row);
    if (key && !byKey.has(key)) byKey.set(key, row);
  });
  return [...byKey.values()];
};

/** The confirm text, with the appointment line when one load brings one. */
export const getConfirmMessage = (rows) => {
  const lines = [
    `Link ${rows.length} loads so they ship together? They will share one pickup appointment and one gate check-in.`,
  ];
  const appointments = getDistinctAppointments(rows);
  if (
    appointments.length === 1 &&
    rows.some((row) => !appointmentKey(row))
  ) {
    lines.push(
      `The others will take the appointment of PO ${appointments[0].poNumber}.`,
    );
  }
  return lines.join(" ");
};

/**
 * Bulk action for the Advices and Pending tabs of the manager and customer
 * Outbound Lists (AD-8): links the selected rows into one ships-together
 * group. Disabled, with the reason as a tooltip, whenever the lambda would
 * refuse; confirmed through ConditionalConfirm; the lambda's user-facing
 * refusal is shown verbatim, anything else goes through logError.
 */
export default function ShipsTogetherButton(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const { data, selectedIds, onUnselectItems } = useListContext(props);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const ids = selectedIds || [];
  const rows = ids.map((id) => data?.[id]).filter(Boolean);
  const blocker = getShipsTogetherBlocker(rows, ids.length);

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (getDistinctAppointments(rows).length > 1) {
      notify(UNSCHEDULE_FIRST_MESSAGE, "warning");
      return;
    }
    setOpen(true);
  };

  const handleConfirm = async () => {
    setOpen(false);
    setSaving(true);
    try {
      const reloadYardId = rows.find((row) => row.reloadYardId)?.reloadYardId;
      await linkOutbounds(ids, { reloadYardId });
      notify("Loads linked");
      onUnselectItems();
      refresh();
    } catch (error) {
      if (isUserFacingError(error)) notify(error.message, "warning");
      else logError("shipWith:ShipsTogetherButton:link", error, notify);
    } finally {
      setSaving(false);
    }
  };

  const button = (
    <Button
      label="Ships Together"
      color="default"
      disabled={Boolean(blocker) || saving}
      onClick={handleClick}
    >
      <LinkIcon />
    </Button>
  );

  return (
    <>
      {blocker ? (
        // A disabled button fires no pointer events, so the tooltip listens
        // on a wrapper.
        <Tooltip title={blocker} arrow>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <ConditionalConfirm
        isOpen={open}
        loading={saving}
        title="Ships Together"
        content={getConfirmMessage(rows)}
        confirm="Link"
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
